import React, { useState, useRef, useEffect } from 'react';
import { Users, Star, CheckCircle, Search, Globe, Youtube, BookOpen, CalendarCheck, ArrowRight, ExternalLink, Award, PlayCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import DashboardLayout from '../components/DashboardLayout';

// === 3D TILT CARD COMPONENT ===
const TiltCard = ({ children, className, depth = 5, glowColor = "rgba(251, 113, 133, 0.15)" }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -depth;
    const rotateY = ((x - centerX) / centerX) * depth;

    card.style.setProperty('--mouse-x', `${x}px`);
    card.style.setProperty('--mouse-y', `${y}px`);
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg)`;
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`relative transition-all duration-300 ease-out preserve-3d group ${className}`}
      style={{
        backgroundImage: `radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), ${glowColor}, transparent 40%)`
      }}
    >
      {children}
    </div>
  );
};

// --- POPULAR MENTORS & CREATORS (Social/YouTube) ---
const socialMentors = [
  { id: 'sm1', name: 'CA Rachana Ranade', expertise: 'Analysis', domain: 'Equity', initials: 'RR', bio: 'Simplifying stock markets for beginners. Known for comprehensive tutorials and market breakdowns.', link: 'https://www.youtube.com/@CA_Rachana_Phadke_Ranade', linkType: 'YouTube', rating: 4.9 },
  { id: 'sm2', name: 'Akshat Shrivastava', expertise: 'Macro Strategy', domain: 'Consulting', initials: 'AS', bio: 'Macroeconomics & strategic investing insights. Focuses on wealth compounding and career strategy.', link: 'https://www.youtube.com/@AkshatShrivastava', linkType: 'YouTube', rating: 4.8 },
  { id: 'sm3', name: 'Pranjal Kamra', expertise: 'Value Investing', domain: 'Finance', initials: 'PK', bio: 'Finology founder, focus on ethical investing and fundamental company analysis.', link: 'https://www.youtube.com/@FinologyVentures', linkType: 'YouTube', rating: 4.9 },
  { id: 'sm4', name: 'Ankur Warikoo', expertise: 'Mindset', domain: 'Personal Growth', initials: 'AW', bio: 'Money management & career growth mentor. Expert in financial discipline and life systems.', link: 'https://www.youtube.com/@warikoo', linkType: 'YouTube', rating: 4.7 },
  { id: 'sm5', name: 'Labor Law Advisor', expertise: 'Tax & Laws', domain: 'Legal', initials: 'LL', bio: 'Simplified labor laws, payroll advice, and central government scheme awareness.', link: 'https://www.youtube.com/@LabourLawAdvisor', linkType: 'YouTube', rating: 4.8 },
  { id: 'sm6', name: 'Asset Yogi', expertise: 'Real Estate', domain: 'Property', initials: 'AY', bio: 'Real estate investing & home loan masterclasses. Focuses on physical assets and long-term wealth.', link: 'https://assetyogi.com', linkType: 'Website', rating: 4.9 },
  { id: 'sm7', name: 'Sandeep Jain', expertise: 'Market Analysis', domain: 'Trading', initials: 'SJ', bio: 'Director at Tradeswift, known for "Gems" picks and technical market trends.', link: 'https://www.youtube.com/@TradeSwift', linkType: 'YouTube', rating: 4.6 },
  { id: 'sm8', name: 'Basant Maheshwari', expertise: 'Growth Investing', domain: 'Equity', initials: 'BM', bio: 'Identifying high-growth companies with extreme focus on management quality.', link: 'https://www.basantmaheshwariwealthadvisers.com', linkType: 'Website', rating: 4.8 },
  { id: 'sm9', name: 'Parimal Ade', expertise: 'Financial Planning', domain: 'Mutual Funds', initials: 'PA', bio: 'Research-backed advice on funds, insurance, and long-term goal planning through Yadnya.', link: 'https://investyadnya.in', linkType: 'Platform', rating: 4.9 },
  { id: 'sm10', name: 'Finance with Sharan', expertise: 'Money Hacks', domain: 'Personal Finance', initials: 'SH', bio: 'Sharan Hegde, simplifying complex finance with humor and creative storytelling.', link: 'https://www.youtube.com/@FinanceWithSharan', linkType: 'YouTube', rating: 4.8 },
  { id: 'sm11', name: 'Neha Nagar', expertise: 'Taxation', domain: 'Business Finance', initials: 'NN', bio: 'Simplifying tax planning, startup funding, and modern investment avenues.', link: 'https://www.youtube.com/@NehaNagar', linkType: 'YouTube', rating: 4.7 },
  { id: 'sm12', name: 'Anant Ladha', expertise: 'Market Trends', domain: 'Analysis', initials: 'AL', bio: 'Invest Aaj For Kal founder. Daily market updates and actionable stock insights.', link: 'https://www.youtube.com/@Anantladhaofficial', linkType: 'YouTube', rating: 4.9 },
  { id: 'sm13', name: 'Graham Stephan', expertise: 'Real Estate/Finance', domain: 'Global Finance', initials: 'GS', bio: 'Global perspective on wealth building, real estate, and economic cycles.', link: 'https://www.youtube.com/@GrahamStephan', linkType: 'YouTube', rating: 4.9 },
  { id: 'sm14', name: 'Mark Tilbury', expertise: 'Mindset/Wealth', domain: 'Entrepreneurship', initials: 'MT', bio: 'Self-made millionaire sharing insights on scaling businesses and investment psychology.', link: 'https://www.youtube.com/@MarkTilbury', linkType: 'YouTube', rating: 4.8 },
  { id: 'sm15', name: 'Jaspreet Singh', expertise: 'Financial Liberty', domain: 'Strategy', initials: 'JS', bio: 'Minority Mindset founder. Teaching financial education that schools skipped.', link: 'https://www.youtube.com/@MinorityMindset', linkType: 'YouTube', rating: 4.9 }
];

// --- BOOKING CONSULTANTS (Professional RIAs/Firms) ---
const bookingAdvisors = [
  { id: 'ba1', name: 'Melvin Joseph', firm: 'Finvin Financial Planners', expertise: 'Fee-Only Planning', initials: 'MJ', bio: 'India\'s first SEBI RIA. Known for unbiased, flat-fee comprehensive financial planning.', link: 'https://www.finvin.in', status: 'Available', rating: 5.0 },
  { id: 'ba2', name: 'Suresh Sadagopan', firm: 'Ladder7 Wealth Planners', expertise: 'Holistic Planning', initials: 'SS', bio: 'Leading RIA focusing on life planning, risk management, and holistic wealth strategies.', link: 'https://www.ladder7.co.in', status: 'Available', rating: 4.9 },
  { id: 'ba3', name: 'Deepak Shenoy', firm: 'Capitalmind', expertise: 'Quant Strategies', initials: 'DS', bio: 'Founder of Capitalmind Wealth. Expert in quantitative investment and portfolio management.', link: 'https://www.capitalmind.in', status: 'In Call', rating: 4.9 },
  { id: 'ba4', name: 'Harsh Roongta', firm: 'Fee Only Investment Advisers', expertise: 'Personal Finance', initials: 'HR', bio: 'Veteran advisor and author. Specialist in real-world personal finance and policy impact.', link: 'https://www.harshroongta.com', status: 'Available', rating: 5.0 },
  { id: 'ba5', name: 'Lovaii Navlakhi', firm: 'Intl Money Matters', expertise: 'Wealth Management', initials: 'LN', bio: 'Managing transition planning and cross-border family wealth for HNIs.', link: 'https://www.immpl.com', status: 'Busy', rating: 4.8 },
  { id: 'ba6', name: 'Pattabiraman Murari', firm: 'FreeFinCal', expertise: 'Robo-Advisory', initials: 'PM', bio: 'The "Prof" behind Freefincal. Analytical tools and conflict-free financial wisdom.', link: 'https://freefincal.com', status: 'Available', rating: 4.9 },
  { id: 'ba7', name: 'Feroze Azeez', firm: 'Anand Rathi Wealth', expertise: 'HNI Wealth', initials: 'FA', bio: 'Professional expertise in large-scale private wealth and HNI portfolio optimization.', link: 'https://www.anandrathiwealth.in', status: 'Available', rating: 4.7 },
  { id: 'ba8', name: 'Dev Ashish', firm: 'StableInvestor', expertise: 'Goal-Based Planning', initials: 'DA', bio: 'Specialist in goal-linked asset allocation and simplified retirement planning.', link: 'https://stableinvestor.com', status: 'Available', rating: 4.8 },
  { id: 'ba9', name: 'Monika Halan', firm: 'Financial Expert', expertise: 'Systems Design', initials: 'MH', bio: 'Author of "Let\'s Talk Money." Designing simple frameworks for personal money systems.', link: 'https://monikahalan.in', status: 'Booked', rating: 5.0 },
  { id: 'ba10', name: 'Shilpa Wagh', firm: 'Fee-Only Planner', expertise: 'Portfolio Analysis', initials: 'SW', bio: 'Expert in mutual fund audit, tax-efficient planning, and estate management.', link: 'https://shilpawagh.com', status: 'Available', rating: 4.9 },
  { id: 'ba11', name: 'Preeti Zende', firm: 'Apana Dhan Services', expertise: 'Wealth Solutions', initials: 'PZ', bio: 'Providing objective, conflict-free advice for long-term financial security.', link: 'http://apanadhan.com', status: 'Available', rating: 4.8 },
  { id: 'ba12', name: 'Vivek Rege', firm: 'VR Wealth Advisors', expertise: 'Retirement Specialist', initials: 'VR', bio: 'Focus on retirement planning and disciplined long-term investing principles.', link: 'https://www.vrwealth.in', status: 'Available', rating: 4.9 },
  { id: 'ba13', name: 'Tarun Birani', firm: 'TBNG Capital', expertise: 'Capital Management', initials: 'TB', bio: 'Expert in global asset allocation and risk-mitigated wealth creation.', link: 'https://tbng.co.in', status: 'Available', rating: 4.6 },
  { id: 'ba14', name: 'Kalpesh Ashar', firm: 'Full Circle Advisors', expertise: 'Comprehensive Planning', initials: 'KA', bio: 'Addressing personal, legacy, and philanthropic financial goals through planning.', link: 'https://www.fullcircleadvisors.in', status: 'Busy', rating: 4.7 },
  { id: 'ba15', name: 'Basavaraj Tonagatti', firm: 'BasuNivesh', expertise: 'DIY Finance Mentor', initials: 'BT', bio: 'Certified Financial Planner focusing on empowering DIY investors with tools.', link: 'https://www.basunivesh.com', status: 'Available', rating: 4.8 }
];

const Consultants = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filterList = (list) => {
    return list.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.expertise.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.firm && item.firm.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.domain && item.domain.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const filteredMentors = filterList(socialMentors);
  const filteredAdvisors = filterList(bookingAdvisors);

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-slate-950 p-4 md:p-8 font-jakarta selection:bg-[#FFB38E]/30 overflow-x-hidden text-[#F8FAFC]">

        {/* === CUSTOM STYLES === */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
          .font-jakarta { font-family: 'Plus Jakarta Sans', sans-serif; }
          
          .skin-shiny-border {
            border: 1px solid rgba(255, 179, 142, 0.2);
            transition: all 0.3s ease;
          }
          
          .skin-shiny-border:hover {
            border-color: rgba(255, 179, 142, 0.5);
            box-shadow: 0 0 15px rgba(255, 179, 142, 0.1);
          }

          .glass-panel {
            background: rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(12px);
            border: 1px solid rgba(255, 255, 255, 0.03);
          }

          .experts-ring {
            border: 1px solid rgba(255, 179, 142, 0.3);
            background: rgba(0, 0, 0, 0.8);
          }

          @keyframes pulse-dot {
            0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
            70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
            100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
          }
          .animate-pulse-live { animation: pulse-dot 2s infinite; }

          .custom-scrollbar::-webkit-scrollbar { width: 4px; }
          .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
          .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 179, 142, 0.2); border-radius: 10px; }
        `}</style>

        <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-[#F8FAFC]">
          <div className="animate-in fade-in slide-in-from-left duration-700">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-[#FFB38E]/10 text-[#FFB38E] text-[8px] font-black uppercase tracking-[0.3em] rounded-full border border-[#FFB38E]/20">
                Mentoring Hub
              </span>
            </div>
            <h1 className="text-xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight uppercase">
              Financial <span className="text-[#FFB38E] drop-shadow-[0_0_10px_rgba(255,179,142,0.3)]">Literacy</span>
            </h1>
            <p className="text-[#94A3B8] text-xs mt-2 font-medium max-w-xl leading-relaxed italic">
              "Mastering your wealth begins with the right guidance. Explore shared wisdom from top educators or book elite professional consultations."
            </p>
          </div>
        </div>

        <Tabs defaultValue="mentors" className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4">
            <TabsList className="bg-black p-1 border border-white/5 rounded-xl h-auto self-start">
              <TabsTrigger
                value="mentors"
                className="rounded-lg px-4 py-1.5 data-[state=active]:bg-zinc-900 data-[state=active]:text-[#FFB38E] data-[state=active]:border data-[state=active]:border-[#FFB38E]/30 text-slate-400 text-[11px] font-bold uppercase tracking-wider flex gap-2 transition-all border border-transparent"
              >
                <Youtube size={14} /> Mentors
              </TabsTrigger>
              <TabsTrigger
                value="advisors"
                className="rounded-lg px-4 py-1.5 data-[state=active]:bg-zinc-900 data-[state=active]:text-[#FFB38E] data-[state=active]:border data-[state=active]:border-[#FFB38E]/30 text-slate-400 text-[11px] font-bold uppercase tracking-wider flex gap-2 transition-all border border-transparent"
              >
                <Award size={14} /> Advisors
              </TabsTrigger>
            </TabsList>

            <div className="relative group min-w-[280px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#FFB38E]" size={16} />
              <Input
                placeholder="Search mentors or expertise..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10 bg-black border border-white/10 rounded-full text-white placeholder:text-slate-600 focus:border-[#FFB38E]/40 focus:ring-2 focus:ring-[#FFB38E]/5 transition-all text-xs font-medium"
              />
            </div>
          </div>

          {/* --- TAB 1: MENTORS --- */}
          <TabsContent value="mentors" className="mt-0 outline-none">
            <Card className="p-4 glass-panel rounded-2xl mb-8 overflow-hidden relative group border-none">
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center text-[#FFB38E] border border-[#FFB38E]/10">
                  <PlayCircle size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-[#F8FAFC] text-sm uppercase tracking-wide">Learn from the Best</h3>
                  <p className="text-[#94A3B8] text-[11px] font-medium leading-tight">
                    Trusted financial voices specializing in market analysis, tax planning, and growth psychology.
                  </p>
                </div>
              </div>
            </Card>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-1">
              {filteredMentors.map((leader) => (
                <TiltCard key={leader.id} className="p-5 bg-black rounded-2xl skin-shiny-border shadow-lg flex flex-col group/card hover:border-[#FFB38E]/30 transition-colors">
                  <div className="flex justify-between items-start mb-4">
                    <div className="relative">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg text-[#FFB38E] experts-ring`}>
                        {leader.initials}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-slate-900 rounded-full flex items-center justify-center border border-white/10">
                        <Youtube size={10} className="text-[#FFB38E]" />
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-[#FFB38E] px-2 py-0.5 border border-[#FFB38E]/20 rounded-md">
                        {leader.domain}
                      </span>
                      <div className="flex items-center gap-0.5 mt-1">
                        <Star size={10} className="text-amber-400/80 fill-amber-400/80" />
                        <span className="text-[10px] text-slate-500 font-bold">{leader.rating}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-2">
                    <h3 className="text-sm font-bold text-[#F8FAFC] leading-none mb-1 group-hover/card:text-[#FFB38E] transition-colors">{leader.name}</h3>
                    <p className="text-[10px] text-[#FFB38E]/80 font-bold uppercase tracking-wider">{leader.expertise}</p>
                  </div>

                  <p className="text-[11px] text-[#94A3B8] mb-6 font-medium leading-relaxed flex-1 line-clamp-3">
                    {leader.bio}
                  </p>

                  <Button
                    className="w-full h-9 bg-black border border-[#FFB38E]/20 text-[#FFB38E] font-bold text-[11px] uppercase tracking-wider rounded-lg hover:bg-zinc-900 transition-all flex items-center justify-center gap-2"
                    onClick={() => window.open(leader.link, '_blank')}
                  >
                    {leader.linkType === 'YouTube' ? <Youtube size={14} /> : <Globe size={14} />}
                    {leader.linkType === 'YouTube' ? 'Watch' : 'Visit'}
                  </Button>
                </TiltCard>
              ))}
            </div>
            {filteredMentors.length === 0 && (
              <div className="text-center py-12 bg-slate-900/10 rounded-2xl border border-white/5">
                <Search size={32} className="mx-auto text-slate-800 mb-3" />
                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">No mentors found.</p>
              </div>
            )}
          </TabsContent>

          {/* --- TAB 2: ADVISORS --- */}
          <TabsContent value="advisors" className="mt-0 outline-none">
            <Card className="p-4 glass-panel rounded-2xl mb-8 overflow-hidden relative group border-none">
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center text-[#FFB38E] border border-[#FFB38E]/10">
                  <CalendarCheck size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-[#F8FAFC] text-sm uppercase tracking-wide">Professional Advice</h3>
                  <p className="text-[#94A3B8] text-[11px] font-medium leading-tight">
                    Book one-on-one sessions with SEBI Registered Investment Advisers (RIAs) and CFPs.
                  </p>
                </div>
              </div>
            </Card>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 px-1">
              {filteredAdvisors.map((advisor) => (
                <TiltCard key={advisor.id} className="p-6 bg-black rounded-2xl skin-shiny-border shadow-xl flex flex-col group/card hover:border-[#FFB38E]/30 transition-all duration-300">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-bold text-xl text-[#FFB38E] experts-ring`}>
                          {advisor.initials}
                        </div>
                        <div className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-slate-900 ${advisor.status === 'Available' ? 'bg-emerald-500 animate-pulse-live' : 'bg-rose-500'}`}></div>
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-[#F8FAFC] leading-tight group-hover/card:text-[#FFB38E] transition-colors">{advisor.name}</h3>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">{advisor.firm}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 border border-[#FFB38E]/20 px-1.5 py-0.5 rounded">
                      <Star size={10} className="text-[#FFB38E]/80 fill-[#FFB38E]/80" />
                      <span className="text-[10px] text-[#FFB38E]/80 font-bold">{advisor.rating.toFixed(1)}</span>
                    </div>
                  </div>

                  <div className="mb-6 flex-1">
                    <div className="inline-block px-1.5 py-0.5 border border-[#FFB38E]/20 rounded text-[9px] font-bold text-[#FFB38E]/80 uppercase tracking-wider mb-2">
                      {advisor.expertise}
                    </div>
                    <p className="text-xs text-[#94A3B8] leading-relaxed font-medium">
                      {advisor.bio}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-white/5 flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                      <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">Status</span>
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${advisor.status === 'Available' ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {advisor.status}
                      </span>
                    </div>
                    <Button
                      className="flex-1 h-10 bg-black border border-[#FFB38E]/30 text-[#FFB38E] font-bold text-[11px] uppercase tracking-wider rounded-lg hover:bg-zinc-900"
                      onClick={() => window.open(advisor.link, '_blank')}
                    >
                      Book Slot
                    </Button>
                  </div>
                </TiltCard>
              ))}
            </div>
            {filteredAdvisors.length === 0 && (
              <div className="text-center py-12 bg-slate-900/10 rounded-2xl border border-white/5">
                <Users size={32} className="mx-auto text-slate-800 mb-3" />
                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">No advisors found.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Consultants;